/**
 * Hook for managing map view state (bounds and zoom)
 */

import { useEffect, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

import { DEFAULT_MAP_BOUNDS, MAP_VIEW_DEBOUNCE_DELAY } from '../utils/constants';
import { isValidBounds, isValidZoom } from '../utils/validation';

export const useMapView = () => {
  const map = useMap();
  const [bounds, setBounds] = useState<[number, number, number, number]>(DEFAULT_MAP_BOUNDS);
  const [zoom, setZoom] = useState(1);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!map) return;

    const updateView = () => {
      // Clear any pending update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Debounce the view update to prevent excessive state changes and API calls
      updateTimeoutRef.current = setTimeout(() => {
        try {
          const mapBounds = map.getBounds();
          if (mapBounds) {
            const ne = mapBounds.getNorthEast();
            const sw = mapBounds.getSouthWest();

            // Validate bounds before setting (simplified validation)
            const newBounds: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

            if (isValidBounds(newBounds)) {
              setBounds(newBounds);
            }
          }

          const currentZoom = map.getZoom();
          if (isValidZoom(currentZoom)) {
            setZoom(currentZoom);
          }
        } catch (error) {
          console.error('Error updating map view:', error);
        }
      }, MAP_VIEW_DEBOUNCE_DELAY);
    };

    // Initial update without debounce for immediate rendering
    try {
      const mapBounds = map.getBounds();
      if (mapBounds) {
        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();
        const initialBounds: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
        if (isValidBounds(initialBounds)) {
          setBounds(initialBounds);
        }
      }
      const currentZoom = map.getZoom();
      if (isValidZoom(currentZoom)) {
        setZoom(currentZoom);
      }
    } catch (error) {
      console.error('Error setting initial map view:', error);
    }

    map.addListener('bounds_changed', updateView);
    map.addListener('zoom_changed', updateView);

    return () => {
      // Clear timeout on cleanup
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      google.maps.event.clearListeners(map, 'bounds_changed');
      google.maps.event.clearListeners(map, 'zoom_changed');
    };
  }, [map]);

  return { mapInstance: map, bounds, zoom };
};
